import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useAppSelector } from '../../redux/hooks';
import { selectProduct } from '../../redux/slices/customizer/customizerSlice';
import { ITab, ITabs } from '../../types/customizer';
import MyGallery from '../MyGallery';
import If from '../Shared/If';
import './index.scss';
import { shouldHideSKU } from '../../utils/basic';
import PrintSummary from '../PrintSummary';
import { getSelectedInfo } from '../../utils/commonRingUtils';
import axios from 'axios';
import { toast } from 'react-toastify';

type SummaryProps = {
  tabs: ITabs;
  isPDP?: boolean;
  pathPrefix?: string;
  navigateToUrl: (url: string, replace?: boolean) => void;
};

export const REQUIRED_TAB_FIELDS = ['ringSize', 'engraving'];
export const TABS_TO_AVOID = ['summary', 'data', 'weight', 'none', 'placement'];

export const getRowContent = (tab: ITab, index: number, options: { [key: string]: boolean } = {}) => {
  const selectedOption = getSelectedInfo(tab);
  const selectedText = selectedOption?.text || '';
  let title = typeof tab.title === 'string' ? tab.title : typeof tab.title === 'function' && tab.title();

  if (tab.eventKey === 'shape') {
    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        {filteredTabs.map((item, index) => (
          <div className="tw-mt-1" key={index}>
            <p>{item.label || ''}</p>
            <p>{item.content?.selectedOption?.short_name || item.content?.selectedOption?.name}</p>
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'finish' && selectedText.includes(' - ')) {
    const [firstFinish, secondFinish] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>First Finish</p>
          <p>{firstFinish}</p>
        </div>
        <div className="tw-mt-1">
          <p>Second Finish</p>
          <p>{secondFinish}</p>
        </div>
      </>
    );
  }

  if (['outsideFeature_1', 'outsideFeature_2'].includes(tab.eventKey)) {
    if (tab.eventKey === 'outsideFeature_2') {
      title = 'Secondary Outside Feature';
    }

    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        <div className="tw-mt-3">
          <p>{title}</p>
          <p>{tab.selectedFeature || 'None'}</p>
        </div>
        {filteredTabs.map((item, index) => (
          <div key={index}>
            <p>{item.label || ''}</p>
            <p>{(item.content?.selectedOption?.name || 'None').replace(/(\d+)mm/g, '$1 mm')}</p>
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'insideFeature') {
    return (
      <div className="tw-mt-3">
        <p>{title}</p>
        <p>{selectedText}</p>
      </div>
    );
  }

  if (tab.eventKey === 'logo') {
    const [school, logo, accentColor] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>School</p>
          <p>{school}</p>
        </div>
        <div className="tw-mt-1">
          <p>Logo</p>
          <p>{logo}</p>
        </div>
        <div className="tw-mt-1">
          <p>Accent Color</p>
          <p>{accentColor}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="tw-mt-1">
        <p>{title}</p>
        <p>{selectedText}</p>
      </div>

      {tab.eventKey === 'engraving' && selectedOption?.engravingText && (
        <div className="tw-mt-1">
          <p>Engraving Text</p>
          <p>{selectedOption.engravingText}</p>
        </div>
      )}
    </>
  );
};

const Summary = ({ tabs, isPDP = false, pathPrefix, navigateToUrl }: SummaryProps) => {
  const product = useAppSelector(selectProduct);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState('');

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'Summary',
    bodyClass: 'print-window'
  });

  const handleSearch = async () => {
    try {
      const response: any = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/rings/searchMagicNumber?magicNumber=${value}`
      );
      const customizeUrl = response.data.ring_data?.customize_url;
      if (customizeUrl) {
        navigateToUrl(customizeUrl);
        setValue('');
      } else {
        toast.error(response.data.message, { className: 'toast-error' });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const containerClass = clsx('tw-flex summary tw-text-base', {
    'tw-overflow-y-auto tw-overflow-x-hidden tw-h-full tw-max-h-[calc(100%-72px)] tw-py-5 lg:tw-pt-20 lg:tw-pb-0 tw-px-4 xl:tw-px-32 swatch-filter-border':
      !isPDP,
    'tw-w-full tw-h-full tw-py-1 tw-pb-4 tw-bg-blue-50 tw-text-xs summary-pdp': isPDP
  });

  const innerContainerClass = clsx('tw-text-prussian-blue', {
    'tw-mx-auto lg:tw-mx-0 xl:tw-mr-20 tw-w-full lg:tw-w-1/2 tw-max-w-[520px]': !isPDP,
    'tw-w-full': isPDP
  });

  const REQUIRED_TABS = tabs.filter((tab: ITab) => REQUIRED_TAB_FIELDS.includes(tab.eventKey));
  const ADDITIONAL_TABS = tabs.filter(
    (tab: ITab) => !REQUIRED_TAB_FIELDS.includes(tab.eventKey) && !TABS_TO_AVOID.includes(tab.eventKey)
  );

  return (
    <div className={containerClass}>
      <div className={innerContainerClass}>
        {!isPDP && <p>{product?.description}</p>}
        <div className="tw-mt-3 tw-text-lg tw-font-bold">Required</div>
        <div className="summary-container tw-mt-3 lg:tw-mt-8">
          <div>
            <p>Ring Category Sku</p>
            <p>{product?.helzberg_custom_sku}</p>
          </div>
          <div className="tw-mt-1">
            <p>REFERENCE CODE</p>
            <p>{product.magic_number}</p>
          </div>
          {REQUIRED_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
          <If condition={!shouldHideSKU(pathPrefix)}>
            <div className="tw-mt-3 lg:tw-mt-8">
              <p>SKU</p>
              <p>{product?.sku}</p>
            </div>
          </If>
        </div>

        <div className="tw-mt-3 tw-text-lg tw-font-bold">Additional Details</div>
        <div className="summary-container tw-mt-3">
          {ADDITIONAL_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
          {!!product.tcw && (
            <div className="tw-mt-3 lg:tw-mt-8">
              <p>Total Carat Weight</p>
              <p>{product.tcw}</p>
            </div>
          )}
        </div>
        <>
          <button
            id="print-btn"
            className="tw-mt-8 tw-bg-brand-color hover:tw-bg-brand-color tw-text-tertiary-color hover:tw-text-tertiary-color tw-px-9 tw-py-2 tw-font-medium tw-normal-case secondary-font-family focus:tw-outline-none"
            onClick={handlePrint}
          >
            Print Summary
          </button>
          <PrintSummary tabs={tabs} image={product?.images?.[0]} pathPrefix={pathPrefix} ref={componentRef} />
          <div className="tw-mt-3 tw-flex">
            <input
              type="text"
              name="magic_number"
              placeholder="Search with Reference code"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              className="tw-p-3 tw-border tw-mr-3"
            />
            <button
              id="search-btn"
              className="tw-bg-brand-color tw-text-white hover:tw-bg-brand-color hover:tw-text-white tw-px-9 tw-py-2 tw-font-medium tw-normal-case secondary-font-family focus:tw-outline-none"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
        </>
      </div>
      {!!product?.images?.length && (
        <div className="tw-min-w-[300px] tw-flex-1 gallery_container tw-relative tw-hidden lg:tw-block">
          <MyGallery images={product?.images} />
        </div>
      )}
    </div>
  );
};

export default Summary;
